import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { taskPanel } from "@/client/components/task-panel-slot";
import { authClient } from "@/client/lib/auth-client";
import { foredayChat } from "@/client/lib/foreday-chat";
import { commandPalette, POPUPS, quickAddSheet } from "@/client/lib/popups";
import { SCREEN_PARTS } from "@/client/lib/screen-parts";

const idle: (run: () => void) => void =
  typeof requestIdleCallback === "function"
    ? (run) => requestIdleCallback(run)
    : (run) => setTimeout(run, 1);

/**
 * A deploy removes the previous build's chunks, and the router reloads the whole page when a
 * screen's chunk is missing. Loaded once the first screen has rendered, every chunk is held for the
 * session, so a deploy is picked up on the next reload rather than in the middle of work. On a
 * phone the chunks would hold up the first screen's own reads, so they wait for those to land; the
 * task panel, opened from every list, does not wait.
 */
export function loadEveryChunk(
  router: AnyRouter,
  queryClient: QueryClient,
): void {
  const stop = router.subscribe("onRendered", () => {
    stop();
    idle(() => void taskPanel.load().catch(() => undefined));
    afterReads(queryClient, () => {
      for (const route of Object.values(router.routesById)) {
        void router.loadRouteChunk(route)?.catch(() => undefined);
      }
      void import("@/client/components/settings-pages")
        .then(({ SETTINGS_PAGES }) =>
          Promise.all(Object.values(SETTINGS_PAGES).map((page) => page.code())),
        )
        .catch(() => undefined);
      for (const popup of [foredayChat, ...POPUPS, ...SCREEN_PARTS]) {
        void popup.load().catch(() => undefined);
      }
      void authClient().catch(() => undefined);
    });
  });
}

/**
 * Quick add and the palette open from keys that work as soon as the shell draws, before the first
 * screen's reads land, so the shell fetches them at its first idle.
 */
export function loadKeyedPopups(): void {
  idle(() => {
    for (const popup of [quickAddSheet, commandPalette]) {
      void popup.load().catch(() => undefined);
    }
  });
}

// Checked again once idle: the reads landing draw the screen, and its components start reads of
// their own in effects after it renders.
function afterReads(queryClient: QueryClient, then: () => void): void {
  idle(() => {
    if (queryClient.isFetching() === 0) {
      then();
      return;
    }
    const stop = queryClient.getQueryCache().subscribe(() => {
      if (queryClient.isFetching() > 0) return;
      stop();
      afterReads(queryClient, then);
    });
  });
}
